import type { Row, RowData } from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';
import { type JSX, type PropsWithChildren } from 'react';

import { getDataTableBorderWidth } from '../../../../utils/data-table-border-width.js';
import { useDataTable } from '../../../contexts/DataTableContext.js';
import type { TDataTableFeatures } from '../../types/features.types.js';
import type { RowParity } from '../row-details-types.js';
import { rowDetailsRow } from '../RowDetails.sty.js';

/**
 * @internal
 */
export function RowDetailsElement<TData extends RowData>(
  props: PropsWithChildren<{
    originalRow: Row<TDataTableFeatures, TData>;
    virtualRow: VirtualItem;
    virtualItemIndex: number;
    rowSeparation: 'none' | 'horizontalDividers' | 'zebraStripes';
    rowParity: RowParity;
    isLastRowOnPage?: boolean;
    rowMeasure?: (node: Element | null) => void;
    isDraggedRow?: boolean;
  }>,
): JSX.Element {
  const {
    originalRow,
    virtualRow,
    virtualItemIndex,
    rowSeparation,
    rowParity,
    rowMeasure,
    isLastRowOnPage: isLastRow,
    isDraggedRow = false,
    children,
  } = props;

  const table = useDataTable();
  let width = table.getRowVirtualizationContainer()?.offsetWidth;

  // if table is in contained variant, subtract table border (left and right)
  if (width && table.atoms.visualVariantConfig?.get().contained) {
    width -= 2 * getDataTableBorderWidth();
  }

  return (
    <div
      role="row"
      id={originalRow.getRowDetailsAriaControlsId()}
      style={{
        ...originalRow.getGridRowPosition(virtualItemIndex),
        ...originalRow.getRowDetailsGridPosition(),
        maxWidth: width,
        position: 'sticky',
        left: 0,
      }}
      className={rowDetailsRow({
        rowSeparation,
        rowParity,
        isDraggedRow,
        isLastRow,
      })}
      ref={rowMeasure}
      data-rowid={originalRow.id}
      // only set the data-row attribute if the element should be considered for measuring (e.g. not in the drag overlay)
      data-row={rowMeasure ? virtualRow.index : undefined}
      data-column={0}
    >
      {children}
    </div>
  );
}
